import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/api";
import { cardCls, btnPrimary } from "../ui/styleTokens";
import { MdCheckCircle, MdOutlineImage, MdOutlineBusiness, MdOutlineDescription, MdOutlinePalette, MdOutlinePerson, MdOutlineCalculate } from "react-icons/md";
import { FiHash } from "react-icons/fi";

const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition";
const labelCls = "block text-xs font-semibold text-gray-700 mb-1.5";

const emptyTemplate = {
    companyName: "Gentium.io",
    address: "TR 01 LETTRE Q N 134 TAMENSOIJRTE, MARRAKECH, MOROCCO",
    phone: "0676541831",
    email: "contact@gentium.io",
    ice: "003239590000044",
    rc: "133559",
    documentTitle: "Facture N°:",
    footerNote: "",
    logoUrl: "",
    primaryColor: "#000000",
    headerBgColor: "#ffffff",
    tableHeaderColor: "#f8f5fc",
    textColor: "#291636",
    showClientIce: true,
    showClientRc: false,
    showTva: true,
    tvaRate: 20,
};

// booleans/numbers that need coercion when they come back from the API/FormData
const BOOL_FIELDS = ["showClientIce", "showClientRc", "showTva"];
const NUM_FIELDS = ["tvaRate"];

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
            <Icon size={16} className="text-gray-400" />
            <h5 className="text-sm font-semibold text-gray-800">{title}</h5>
        </div>
        {children}
    </div>
);

const ColorField = ({ label, value, onChange }) => (
    <div>
        <label className={labelCls}>{label}</label>
        <div className="flex items-center gap-2">
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 border border-gray-200 rounded-lg cursor-pointer shrink-0" />
            <input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
        </div>
    </div>
);

const Toggle = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600" />
        <span className="text-sm text-gray-700">{label}</span>
    </label>
);

const PREVIEW_ITEMS = [
    { desc: "Retro Planning", amount: 1500 },
    { desc: "Badging", amount: 800 },
];

const InvoiceTemplateTab = () => {
    const [template, setTemplate] = useState(emptyTemplate);
    const [original, setOriginal] = useState(emptyTemplate);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        axiosInstance.get("/billing/template")
            .then((r) => {
                const data = { ...emptyTemplate, ...r.data };
                setTemplate(data);
                setOriginal(data);
            })
            .catch((err) => console.error("Failed to load invoice template", err))
            .finally(() => setLoading(false));
    }, []);

    const update = (field, value) => {
        setTemplate((t) => ({ ...t, [field]: value }));
        setSavedAt(null);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setSavedAt(null);
    };

    const isDirty = !!logoFile || Object.keys(emptyTemplate).some((k) => template[k] !== original[k]);

    const handleDiscard = () => {
        setTemplate(original);
        setLogoFile(null);
        setLogoPreview("");
    };

    const handleReset = () => {
        setTemplate(emptyTemplate);
        setLogoFile(null);
        setLogoPreview("");
        setSavedAt(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            Object.keys(emptyTemplate).forEach((key) => {
                if (key === "logoUrl") return;
                formData.append(key, template[key] ?? "");
            });
            if (logoFile) formData.append("logo", logoFile);

            const res = await axiosInstance.put("/billing/template", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const data = { ...emptyTemplate, ...res.data };
            setTemplate(data);
            setOriginal(data);
            setLogoFile(null);
            setLogoPreview("");
            setSavedAt(Date.now());
        } catch (err) {
            console.error("Failed to save invoice template", err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p className="text-sm text-gray-500">Loading template…</p>;

    const previewLogo = logoPreview || template.logoUrl;
    const tvaRate = Number(template.tvaRate) || 0;
    const totalHT = PREVIEW_ITEMS.reduce((s, i) => s + i.amount, 0);
    const totalTVA = template.showTva ? Math.round(totalHT * (tvaRate / 100)) : 0;
    const totalTTC = totalHT + totalTVA;

    return (
        <div className={`${cardCls} p-5 pb-0 relative`}>
            <div className="flex items-baseline justify-between mb-1">
                <h4 className="font-semibold text-gray-900 text-sm">Invoice template</h4>
                <div className="flex items-center gap-3">
                    {savedAt && !isDirty && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <MdCheckCircle size={14} /> Saved
                        </span>
                    )}
                    <button type="button" onClick={handleReset} className="text-xs font-medium text-gray-400 hover:text-red-600 transition">
                        Reset to default
                    </button>
                </div>
            </div>
            <p className="text-sm text-gray-500 mb-5">This branding appears on every invoice you generate.</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-5">
                {/* FORM */}
                <div className="space-y-4">
                    <SectionCard icon={MdOutlineImage} title="Logo">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                                {previewLogo ? (
                                    <img src={previewLogo} alt="Logo" className="w-full h-full object-contain" />
                                ) : (
                                    <MdOutlineImage size={20} className="text-gray-300" />
                                )}
                            </div>
                            <div>
                                <label className="cursor-pointer inline-block px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                                    Upload logo
                                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                                </label>
                                <p className="text-xs text-gray-400 mt-1.5">PNG or JPG</p>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard icon={MdOutlineBusiness} title="Company">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2"><label className={labelCls}>Company name</label><input value={template.companyName} onChange={(e) => update("companyName", e.target.value)} className={inputCls} /></div>
                            <div className="col-span-2"><label className={labelCls}>Address</label><input value={template.address} onChange={(e) => update("address", e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>Phone</label><input value={template.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>Email</label><input value={template.email} onChange={(e) => update("email", e.target.value)} className={inputCls} /></div>
                        </div>
                    </SectionCard>

                    <SectionCard icon={FiHash} title="Legal">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls}>ICE</label><input value={template.ice} onChange={(e) => update("ice", e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>RC</label><input value={template.rc} onChange={(e) => update("rc", e.target.value)} className={inputCls} /></div>
                        </div>
                    </SectionCard>

                    <SectionCard icon={MdOutlinePerson} title="Client info shown on invoice">
                        <div className="flex flex-col gap-2.5">
                            <Toggle label="Show client ICE" checked={template.showClientIce} onChange={(v) => update("showClientIce", v)} />
                            <Toggle label="Show client RC" checked={template.showClientRc} onChange={(v) => update("showClientRc", v)} />
                        </div>
                    </SectionCard>

                    <SectionCard icon={MdOutlineCalculate} title="TVA">
                        <div className="flex items-end gap-4">
                            <Toggle label="Apply TVA to invoices" checked={template.showTva} onChange={(v) => update("showTva", v)} />
                            {template.showTva && (
                                <div className="w-28">
                                    <label className={labelCls}>Rate (%)</label>
                                    <input type="number" min={0} max={100} value={template.tvaRate} onChange={(e) => update("tvaRate", e.target.value)} className={inputCls} />
                                </div>
                            )}
                        </div>
                    </SectionCard>

                    <SectionCard icon={MdOutlinePalette} title="Colors">
                        <div className="grid grid-cols-2 gap-3">
                            <ColorField label="Header background" value={template.headerBgColor} onChange={(v) => update("headerBgColor", v)} />
                            <ColorField label="Accent (title / totals)" value={template.primaryColor} onChange={(v) => update("primaryColor", v)} />
                            <ColorField label="Table header" value={template.tableHeaderColor} onChange={(v) => update("tableHeaderColor", v)} />
                            <ColorField label="Body text" value={template.textColor} onChange={(v) => update("textColor", v)} />
                        </div>
                    </SectionCard>

                    <SectionCard icon={MdOutlineDescription} title="Document">
                        <div className="space-y-3">
                            <div>
                                <label className={labelCls}>Document title</label>
                                <input value={template.documentTitle} onChange={(e) => update("documentTitle", e.target.value)} className={inputCls} placeholder="FACTURE" />
                            </div>
                            <div>
                                <label className={labelCls}>Footer note / payment terms</label>
                                <textarea value={template.footerNote} onChange={(e) => update("footerNote", e.target.value)} rows={3} placeholder="e.g. Payment due within 15 days. RIB: ..." className={`${inputCls} resize-none`} />
                            </div>
                        </div>
                    </SectionCard>
                </div>

                {/* LIVE PREVIEW */}
                <div>
                    <p className={labelCls}>Preview</p>
                    <div
                        className="border border-gray-200 rounded-xl p-6 sticky top-4"
                        style={{ backgroundColor: template.headerBgColor || "#fff", color: template.textColor || "#111827" }}
                    >
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center gap-3">
                                {previewLogo ? (
                                    <img src={previewLogo} alt="Logo" className="h-10 object-contain" />
                                ) : (
                                    <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center">
                                        <MdOutlineImage size={16} className="text-gray-300" />
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-bold">{template.companyName || "Your company"}</p>
                                    <p className="text-xs opacity-70">{template.address || "Company address"}</p>
                                    <p className="text-xs opacity-70">{[template.phone, template.email].filter(Boolean).join(" · ") || "Phone · Email"}</p>
                                    {(template.ice || template.rc) && (
                                        <p className="text-xs opacity-50 mt-0.5">
                                            {[template.ice && `ICE: ${template.ice}`, template.rc && `RC: ${template.rc}`].filter(Boolean).join(" — ")}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <h3 className="text-base font-bold" style={{ color: template.primaryColor || "#2563EB" }}>
                                    {template.documentTitle || "FACTURE"} 001-2026
                                </h3>
                                <p className="text-xs opacity-50 mt-1">{new Date().toLocaleDateString()}</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-[10px] text-gray-500 font-semibold mb-1">
                                Client:
                            </p>

                            <p className="text-sm font-semibold">
                                Client name
                            </p>

                            <p className="text-xs opacity-70 mt-0.5">
                                client@email.com
                            </p>

                            <p className="text-xs opacity-70 mt-0.5">
                                06XXXXXXX
                            </p>

                            <p className="text-xs opacity-70 mt-0.5">
                                Client address
                            </p>

                            {(template.showClientIce || template.showClientRc) && (
                                <p className="text-xs opacity-60 mt-1">
                                    {[
                                        template.showClientIce && "00XXXXXXXXXXXXX",
                                        template.showClientRc && "XXXXX"
                                    ]
                                        .filter(Boolean)
                                        .join(" — ")}
                                </p>
                            )}
                        </div>

                        {/* Products table */}
                        <table className="w-full text-xs border-collapse mb-4">
                            <thead>
                                <tr style={{ backgroundColor: template.tableHeaderColor || "#f8f5fc" }}>
                                    <th className="text-left font-semibold px-3 py-2 rounded-l-lg">Description</th>
                                    <th className="text-right font-semibold px-3 py-2 w-28 rounded-r-lg">Montant HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PREVIEW_ITEMS.map((item, i) => (
                                    <tr key={i} className="border-b border-black/5">
                                        <td className="px-3 py-2">{item.desc}</td>
                                        <td className="px-3 py-2 text-right font-medium">{item.amount.toLocaleString()} MAD</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div className="flex justify-end">
                            <div className="w-56 space-y-1.5">
                                <div className="flex justify-between text-xs opacity-70">
                                    <span>Total HT</span><span>{totalHT.toLocaleString()} MAD</span>
                                </div>
                                {template.showTva && (
                                    <div className="flex justify-between text-xs opacity-70">
                                        <span>TVA ({tvaRate}%)</span><span>{totalTVA.toLocaleString()} MAD</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-1.5 border-t border-black/10">
                                    <span className="text-sm font-semibold">Total TTC</span>
                                    <span className="text-sm font-bold" style={{ color: template.primaryColor || "#2563EB" }}>
                                        {totalTTC.toLocaleString()} MAD
                                    </span>
                                </div>
                            </div>
                        </div>

                        {template.footerNote && (
                            <p className="text-[10px] opacity-50 border-t border-black/10 mt-6 pt-3 whitespace-pre-line">
                                {template.footerNote}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {isDirty && (
                <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-gray-200 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Unsaved changes</span>
                    <div className="flex gap-2">
                        <button onClick={handleDiscard} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition">Discard</button>
                        <button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? "Saving…" : "Save changes"}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceTemplateTab;